import {SupabaseClient, User, createClient} from '@supabase/supabase-js';

import {Database} from '../../types/database.types';

// Create a single instance at the module level with a proper initialization check
let supabaseInstance: SupabaseClient<Database> | null = null;
let isInitializing = false;

/**
 * This class represents setting up and handling all things supabase related.
 */
export class SupabaseClass {
  public supabase: SupabaseClient<Database>;

  constructor() {
    // If we already have an instance or are in the process of creating one, return/wait for it
    if (supabaseInstance) {
      this.supabase = supabaseInstance;
      return;
    }

    if (isInitializing) throw new Error('Supabase client is already being initialized');
    isInitializing = true;

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_KEY;
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase URL or key is not defined');
      }

      supabaseInstance = createClient<Database>(supabaseUrl, supabaseKey, {
        auth: {
          persistSession: true,
          storageKey: 'exchange-supabase-auth',
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      });
      this.supabase = supabaseInstance;
    } finally {
      isInitializing = false;
    }
  }

  /**
   * Gets the current user from the session.
   */
  public getCurrentUser = async (): Promise<User> => {
    const {data, error} = await this.supabase.auth.getUser();
    if (error || !data.user) {
      throw new Error(error?.message || 'No user found');
    }
    return data.user;
  };

  /**
   * Gets the current user's id from the session.
   */
  public getCurrentUserId = async (): Promise<string> => {
    const {id} = await this.getCurrentUser().catch(() => ({id: ''}));
    return id;
  };

  /**
   * Gets the current session.
   *
   */
  public getCurrentSession = async (): Promise<ReturnType<typeof this.supabase.auth.getSession>> => {
    const session = await this.supabase.auth.getSession();
    return session;
  };

  /**
   * Helper function to make RPC calls with proper type inference
   */
  public rpc = async <F extends keyof Database['public']['Functions']>(
    methodName: F,
    args: Database['public']['Functions'][F]['Args'] = {} as Database['public']['Functions'][F]['Args']
  ): Promise<Database['public']['Functions'][F]['Returns']> => {
    const {data, error} = await this.supabase.rpc(methodName, args);

    if (error) {
      console.error(`Error in RPC call to ${methodName}:`, error);
      throw error;
    }

    return data as Database['public']['Functions'][F]['Returns'];
  };
}

// Create and export a single instance
export const SupabaseInstance = new SupabaseClass();

// Export the client for convenience
export const supabase = SupabaseInstance.supabase;
